// components/data-sources/connectorCatalog/ConnectorRequestModal.tsx
/**
 * The request dialog behind an "Available on request" tile (ADR-0410): what
 * the connector imports, an optional note, a Request button, and a
 * confirmation. Who may request is decided by the backend; `canRequest` only
 * explains up front why the button is off.
 */

import { useState } from "react";
import { Alert, Box, Button, Group, List, Stack, Text, Textarea } from "@mantine/core";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";

import { requestConnector } from "@/api/managedSources";
import { AppModal } from "@/components/modals/AppModal";
import { ConnectorIcon } from "@/components/data-sources/ConnectorIcon";
import { useBranding } from "@/context/BrandingContext";
import type { ManagedSourceConnector } from "@/types/managedSources";

import { formatRequestDate } from "./tileState";
import classes from "../AddDataSourceModal.module.css";

export const NOTE_MAX_LENGTH = 500;

type Props = {
  /** The connector being requested; `null` keeps the dialog closed. */
  connector: ManagedSourceConnector | null;
  canRequest: boolean;
  onClose: () => void;
  /** Called once the backend has recorded the request. */
  onRequested: (connectorType: string, requestedAt: string) => void;
};

export function ConnectorRequestModal(props: Props) {
  if (!props.connector) return null;
  // Keyed per connector: a fresh dialog, so a note typed for one connector is
  // never sent for another.
  return (
    <RequestDialog key={props.connector.connector_type} {...props} connector={props.connector} />
  );
}

function RequestDialog({
  connector,
  canRequest,
  onClose,
  onRequested,
}: Props & { connector: ManagedSourceConnector }) {
  const { branding } = useBranding();
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestedAt, setRequestedAt] = useState<string | null>(null);

  const label = connector.label;
  const available = (connector.categories ?? []).filter((c) => c.available);
  const product = branding.productName;
  const description = connector.description.replace(/\bVerity\b/g, product);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const result = await requestConnector(connector.connector_type, note);
      setRequestedAt(result.requested_at);
      onRequested(connector.connector_type, result.requested_at);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Your request could not be sent. Try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  }

  const done = requestedAt !== null;

  return (
    <AppModal
      opened
      onClose={onClose}
      size="sm"
      title={done ? "Request sent" : `Request ${label}`}
      subtitle={done ? undefined : "Available on request"}
      leading={
        <Box className={classes.logo}>
          <ConnectorIcon type={connector.connector_type} size={24} />
        </Box>
      }
      footer={
        done ? (
          <Group justify="flex-end">
            <Button onClick={onClose}>Done</Button>
          </Group>
        ) : (
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} loading={submitting} disabled={!canRequest}>
              Request
            </Button>
          </Group>
        )
      }
    >
      {done ? (
        <Stack gap="sm" data-testid="connector-request-confirmation">
          <Group gap="xs" wrap="nowrap">
            <IconCircleCheck size={20} color="var(--mantine-color-green-6)" />
            <Text fw={600}>
              We received your request for {label} on {formatRequestDate(requestedAt)}.
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            Our team reviews it and enables {label} for your account. Once it is enabled,
            you can connect it from <b>Add data source</b>.
          </Text>
        </Stack>
      ) : (
        <Stack gap="md">
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              What {product} imports
            </Text>
            <Text size="sm" c="dimmed">
              {description}
            </Text>
            {available.length > 0 && (
              <List size="sm" spacing={2} mt={4}>
                {available.map((c) => (
                  <List.Item key={c.key}>{c.label}</List.Item>
                ))}
              </List>
            )}
          </Stack>

          <Text size="sm">
            {label} is available on request. Send a request and our team enables it for
            your account.
          </Text>

          <Textarea
            label="Note (optional)"
            description="Anything we should know, such as which account or store you want to connect."
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            maxLength={NOTE_MAX_LENGTH}
            autosize
            minRows={2}
            maxRows={6}
            disabled={!canRequest || submitting}
          />

          {!canRequest && (
            <Alert color="gray" variant="light">
              Only account owners and admins can request a connector. Ask one of them to
              send the request.
            </Alert>
          )}

          {error && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              {error}
            </Alert>
          )}
        </Stack>
      )}
    </AppModal>
  );
}
